import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, toggleIn, listOf } from "@/lib/api";
import { PageShell } from "@/components/PageShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Trash2, Pencil } from "lucide-react";

const CATS = ["business", "health", "home", "relationship", "project", "random"];
const STATUSES = ["parked", "someday", "active", "archived"];
const emptyForm = { title: "", notes: "", categories: ["random"], status: "parked" };

const statusColor = {
  parked: "bg-zinc-200 text-zinc-600",
  someday: "bg-[#365458]/15 text-[#2A4346]",
  active: "bg-[#365458] text-white",
  archived: "bg-zinc-300 text-zinc-500",
};

export default function ParkingLot() {
  const [ideas, setIdeas] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => setIdeas(await api.listParkingLot()), []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (idea) => {
    setEditing(idea);
    setForm({ title: idea.title, notes: idea.notes || "", categories: listOf(idea, "categories", "category"), status: idea.status });
    setOpen(true);
  };

  const save = async () => {
    if (!form.title.trim()) return;
    if (editing) {
      await api.updateIdea(editing.id, form);
      toast.success("Idea updated.");
    } else {
      await api.createIdea(form);
      toast.success("Parked. It'll be here when you're ready.");
    }
    setOpen(false);
    load();
  };

  const remove = async (id) => { await api.deleteIdea(id); toast("Moved to trash."); load(); };
  const cycleStatus = async (idea) => {
    const next = STATUSES[(STATUSES.indexOf(idea.status) + 1) % STATUSES.length];
    await api.updateIdea(idea.id, { status: next });
    load();
  };

  return (
    <PageShell
      title="Parking lot"
      subtitle="Good ideas that aren't for today."
      action={
        <Button data-testid="add-idea-button" onClick={openNew} className="rounded-xl bg-[#365458] hover:bg-[#2A4346] h-11 w-11 p-0">
          <Plus className="w-5 h-5" />
        </Button>
      }
    >
      {ideas.length === 0 ? (
        <div className="bg-zinc-800/60 rounded-2xl p-6 text-center">
          <p className="text-zinc-400 text-sm">No ideas parked yet. Capture one so it stops bugging you.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="idea-list">
          {ideas.map((idea) => (
            <div key={idea.id} className="bg-zinc-100 rounded-2xl p-4 shadow-md" data-testid={`idea-card-${idea.id}`}>
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-semibold text-zinc-900 leading-snug">{idea.title}</h3>
                <button
                  data-testid={`idea-status-${idea.id}`}
                  onClick={() => cycleStatus(idea)}
                  className={`shrink-0 px-2.5 py-1 rounded-full text-[11px] font-semibold capitalize ${statusColor[idea.status]}`}
                >
                  {idea.status}
                </button>
              </div>
              {idea.notes && <p className="text-sm text-zinc-500 mt-1">{idea.notes}</p>}
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-zinc-200">
                <span className="flex flex-wrap gap-1">
                  {listOf(idea, "categories", "category").map((c) => (
                    <span key={c} className="text-xs text-zinc-500 capitalize px-2 py-0.5 rounded-full bg-zinc-200">{c}</span>
                  ))}
                </span>
                <div className="flex gap-3">
                  <button data-testid={`idea-edit-${idea.id}`} onClick={() => openEdit(idea)} className="text-zinc-500 hover:text-zinc-800">
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button data-testid={`idea-delete-${idea.id}`} onClick={() => remove(idea.id)} className="text-zinc-400 hover:text-[#991B1B]">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-md rounded-3xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">{editing ? "Edit idea" : "Park an idea"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <Input
              data-testid="idea-title-input"
              placeholder="Idea title"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="bg-zinc-800 border-zinc-700 text-zinc-100 h-12 rounded-xl"
            />
            <Textarea
              data-testid="idea-notes-input"
              placeholder="Notes (optional)"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="bg-zinc-800 border-zinc-700 text-zinc-100 rounded-xl resize-none"
            />
            <div>
              <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-2">Category</p>
              <div className="flex flex-wrap gap-2">
                {CATS.map((c) => (
                  <button key={c} data-testid={`idea-cat-${c}`} onClick={() => setForm({ ...form, categories: toggleIn(form.categories, c) })}
                    className={`px-3 py-2 rounded-xl text-sm font-medium capitalize ${form.categories.includes(c) ? "bg-[#365458] text-white" : "bg-zinc-800 text-zinc-300"}`}>
                    {c}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-2">Status</p>
              <div className="flex flex-wrap gap-2">
                {STATUSES.map((s) => (
                  <button key={s} data-testid={`idea-statuschoice-${s}`} onClick={() => setForm({ ...form, status: s })}
                    className={`px-3 py-2 rounded-xl text-sm font-medium capitalize ${form.status === s ? "bg-[#365458] text-white" : "bg-zinc-800 text-zinc-300"}`}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
            <Button data-testid="idea-save-button" onClick={save}
              className="w-full h-14 rounded-2xl bg-[#365458] hover:bg-[#2A4346] text-white text-base font-bold">
              {editing ? "Save changes" : "Park it"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
}
